import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Button, Divider, Header, Message, Segment } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../app/common/modals/modalSlice";
import MyTextInput from "../../../app/common/form/MyTextInput";
import { sendFreeTrialSignupEmail } from "../../../app/firestore/firestoreService";

const validationSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  middleName: Yup.string(),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  license: Yup.string().required("Real estate license number is required"),
  brokerageName: Yup.string().required("Brokerage name is required"),
});

export default function PartyFreeTrialSignupForm() {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [submitted, setSubmitted] = useState(false);

  const initialValues = {
    firstName: currentUserProfile?.firstName || "",
    middleName: currentUserProfile?.middleName || "",
    lastName: currentUserProfile?.lastName || "",
    email: currentUserProfile?.email || "",
    license: "",
    brokerageName: "",
  };

  function handleClose() {
    dispatch(closeModal({ modalType: "PartyFreeTrialSignupForm" }));
  }

  async function handleSubmit(values, { setSubmitting }) {
    try {
      await sendFreeTrialSignupEmail(values, currentUserProfile);
      setSubmitted(true);
    } catch (error) {
      console.error("Error sending free trial signup email:", error);
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalWrapper size="small">
      <Segment>
        {submitted ? (
          <>
            <Header size="large" color="blue">
              Sign Up for Free Trial
            </Header>
            <Divider />
            <Message positive>
              <Message.Header>
                Your free trial is getting set up!
              </Message.Header>
              <p>
                Look for an email from support@transactioner.com for your
                login information.
              </p>
            </Message>
            <Button
              primary
              onClick={handleClose}
              type="button"
              floated={isMobile ? null : "right"}
              content="Close"
              className={isMobile ? "fluid medium bottom margin" : null}
            />
          </>
        ) : (
          <>
            <Header size="large" color="blue">
              Sign Up for Free Trial
            </Header>
            <Divider />
            <p>
              Fill out the information below and we'll get your free trial of
              TransActioner set up.
            </p>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, dirty, isValid }) => (
                <Form className="ui form">
                  <MyTextInput name="firstName" label="First Name" />
                  <MyTextInput name="middleName" label="Middle Name" />
                  <MyTextInput name="lastName" label="Last Name" />
                  <MyTextInput name="email" label="Email" />
                  <MyTextInput name="license" label="Real Estate License #" />
                  <MyTextInput name="brokerageName" label="Brokerage Name" />
                  <Divider />
                  <Button
                    disabled={isSubmitting}
                    onClick={handleClose}
                    type="button"
                    floated={isMobile ? null : "right"}
                    content="Cancel"
                    className={isMobile ? "fluid medium bottom margin" : null}
                  />
                  <Button
                    primary
                    loading={isSubmitting}
                    disabled={isSubmitting || !isValid || !dirty}
                    type="submit"
                    floated={isMobile ? null : "right"}
                    content="Sign Up for Free Trial"
                    className={isMobile ? "fluid medium bottom margin" : null}
                  />
                </Form>
              )}
            </Formik>
          </>
        )}
      </Segment>
    </ModalWrapper>
  );
}
